/**
 * Установка PWA «на экран».
 *
 * На Android/десктопном Chrome браузер даёт событие `beforeinstallprompt` —
 * его можно перехватить и по кнопке показать системный диалог установки.
 * На iOS такого нет вовсе: единственный путь — «Поделиться → На экран „Домой“»,
 * поэтому там показываем инструкцию, а не кнопку.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferred: BeforeInstallPromptEvent | null = null
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

// Слушатели ставим на загрузке модуля — событие приходит рано, до отрисовки.
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault() // иначе Chrome покажет свою мини-плашку сам
  deferred = e as BeforeInstallPromptEvent
  emit()
})
window.addEventListener('appinstalled', () => {
  deferred = null
  emit()
})

/** Есть ли перехваченное приглашение установки (Chromium). */
export const canPromptInstall = (): boolean => deferred !== null

/** Показывает системный диалог установки; возвращает, согласился ли человек. */
export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false
  await deferred.prompt()
  const { outcome } = await deferred.userChoice
  deferred = null
  emit()
  return outcome === 'accepted'
}

/** Приложение уже открыто как установленное (standalone). */
export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari сообщает об установленном режиме через нестандартное поле.
    (navigator as unknown as { standalone?: boolean }).standalone === true
  )
}

/** iOS (там нет beforeinstallprompt — только ручная установка). */
export function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent)
}

/** Подписка на изменения (появилось приглашение / установили). */
export function onInstallChange(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}
